import accessTokenService from 'utils/accessTokenService';
import { redirectToIdPortalLogout } from 'utils/redirectToIdPortalLogout';

const KINETIK_ACCESS_TOKEN_ENDPOINT = '/auth/user/access-token';

// Shared across every axios instance created via this factory so concurrent
// refresh attempts from different instances coordinate on the same in-flight
// request instead of firing duplicate refresh calls.
let isRefreshingAccessToken = false;
let accessTokenRefreshRequestPromises = [];

/**
 * Builds a `refreshAccessToken` function bound to the given axios instance.
 *
 * Concurrent callers coordinate on a single in-flight refresh request. On
 * failure (the refresh token / session has genuinely expired), the stale
 * access token is cleared and the user is redirected to the ID portal to
 * re-authenticate — `finally` guarantees `isRefreshingAccessToken` always
 * resets and queued callers always settle, even when the refresh request
 * itself throws (axios rejects on any non-2xx response by default).
 */
export const createAccessTokenRefresher = (kinetikApiInstance) => async () => {
  if (isRefreshingAccessToken) {
    return new Promise((resolve, reject) => {
      accessTokenRefreshRequestPromises.push({ resolve, reject });
    });
  }

  isRefreshingAccessToken = true;

  try {
    const accessTokenResponse = await kinetikApiInstance.post(
      KINETIK_ACCESS_TOKEN_ENDPOINT,
      null,
      { withCredentials: true, serviceName: 'account' },
    );

    const newAccessToken = accessTokenResponse.data.accessToken.token;
    accessTokenService.setAccessToken(newAccessToken);

    accessTokenRefreshRequestPromises.forEach(({ resolve }) =>
      resolve(newAccessToken),
    );
    accessTokenRefreshRequestPromises = [];

    return newAccessToken;
  } catch (error) {
    accessTokenService.removeAccessToken();
    redirectToIdPortalLogout();

    accessTokenRefreshRequestPromises.forEach(({ reject }) => reject(error));
    accessTokenRefreshRequestPromises = [];

    throw error;
  } finally {
    isRefreshingAccessToken = false;
  }
};

export { KINETIK_ACCESS_TOKEN_ENDPOINT };
